// apiService.js - Backend API integration with Supabase auth - FIXED
import { supabase } from './supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com/api';

// ✅ FIXED: Get token directly from localStorage
const getAuthToken = async () => {
  try {
    const authKey = Object.keys(localStorage).find(key => 
      key.includes('supabase.auth.token')
    );
    
    if (!authKey) {
      console.warn('No auth key in localStorage');
      return null;
    }
    
    const authData = localStorage.getItem(authKey);
    if (!authData) {
      console.warn('No auth data in localStorage');
      return null;
    }
    
    const session = JSON.parse(authData);
    
    if (!session.access_token) {
      console.warn('No access token in session');
      return null;
    }
    
    const token = session.access_token.trim();
    const segments = token.split('.');
    
    if (segments.length !== 3) {
      console.error('Invalid token format');
      return null;
    }
    
    console.log('✅ Token retrieved from localStorage');
    return token;
    
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// ✅ Get current user ID from Supabase
const getUserId = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id;
};

// Helper function to make API calls
const apiCall = async (endpoint, options = {}) => {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  let data;
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = { error: text, message: text };
  }
  
  if (!response.ok) {
    throw new Error(data.error || data.message || 'API request failed');
  }
  
  return data;
};

// Auth APIs
export const authAPI = {
  register: async (userData) => {
    return apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },
  
  login: async (credentials) => {
    return apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },
  
  logout: async () => {
    return apiCall('/auth/logout', {
      method: 'POST',
    });
  },
  
  me: async () => {
    return apiCall('/auth/me');
  }
};

export const walletAPI = {
  getAllWallets: async () => {
    return apiCall('/wallet/all');
  },
  
  getWallet: async () => {
    return apiCall('/wallet');
  },
  
  createWallet: async (walletData) => {
    return apiCall('/wallet', {
      method: 'POST',
      body: JSON.stringify(walletData),
    });
  },
  
  getBalance: async () => {
    return apiCall('/wallet/balance');
  },
  
  getTransactions: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return apiCall(`/wallet/transactions?${params}`);
  },
  
  topUpWithCard: async (cardData) => {
    return apiCall('/wallet/topup-card', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });
  },
  
  depositUSD: async (amount) => {
    return apiCall('/wallet/deposit', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },
  
  withdrawBTC: async (withdrawData) => {
    return apiCall('/wallet/withdraw', {
      method: 'POST',
      body: JSON.stringify(withdrawData),
    });
  },
  
  sendFromWallet: async (sendData) => {
    return apiCall('/wallet/send', {
      method: 'POST',
      body: JSON.stringify(sendData),
    });
  },
  
  getPrivateKey: async (walletId, pin) => {
    return apiCall('/wallet/private-key', {
      method: 'POST',
      body: JSON.stringify({ walletId, pin }),
    });
  }
};

// ✅ Hot Wallet API
export const hotWalletAPI = {
  getBalance: async () => {
    return apiCall('/wallet/hot-wallet/balance');
  }
};

export const cardAPI = {
  createCard: async (cardData) => {
    return apiCall('/cards', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });
  },
  
  // ✅ FIXED: Use card-wallet endpoint for updated balances
  getCards: async () => {
    const userId = await getUserId();
    if (!userId) {
      console.warn('No user ID, falling back to /cards');
      return apiCall('/cards');
    }
    
    const timestamp = Date.now();
    const response = await apiCall(`/card-wallet/user/${userId}/cards?t=${timestamp}`);
    
    // Wrap response to match expected format
    return {
      success: response.success,
      data: {
        cards: response.cards || []
      }
    };
  },
  
  getActivatedCards: async () => {
    const userId = await getUserId();
    if (!userId) {
      return apiCall('/cards/activated');
    }
    
    const timestamp = Date.now();
    const response = await apiCall(`/card-wallet/user/${userId}/cards?t=${timestamp}`);
    
    return {
      success: response.success,
      data: {
        cards: response.cards || []
      }
    };
  },
  
  // ✅ FIXED: Accept 2 parameters
  activateCard: async (cardNumber, pin) => {
    return apiCall('/cards/activate', {
      method: 'POST',
      body: JSON.stringify({ cardNumber, pin }),
    });
  },
  
  getCardWallet: async (cardId) => {
    return apiCall(`/cards/${cardId}/wallet`);
  },
  
  sendFromCard: async (cardId, sendData) => {
    return apiCall(`/cards/${cardId}/send`, {
      method: 'POST',
      body: JSON.stringify(sendData),
    });
  }
};
// Add this NEW section for payments
export const paymentsAPI = {
  calculateConversion: async (data) => {
    return apiCall('/payments/calculate-conversion', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createPlaidLinkToken: async () => {
    return apiCall('/payments/plaid/create-link-token', {
      method: 'POST',
    });
  },

  exchangePlaidToken: async (data) => {
    return apiCall('/payments/plaid/exchange-token', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getBankAccounts: async () => {
    return apiCall('/payments/bank-accounts');
  },

  addBankAccount: async (bankData) => {
    return apiCall('/payments/bank-accounts', {
      method: 'POST',
      body: JSON.stringify(bankData),
    });
  },

  sendWithCrypto: async (transferData) => {
    return apiCall('/payments/send/crypto', {
      method: 'POST',
      body: JSON.stringify(transferData),
    });
  },

  sendWithBank: async (transferData) => {
    return apiCall('/payments/send/bank', {
      method: 'POST',
      body: JSON.stringify(transferData),
    });
  },

  sendWithCard: async (transferData) => {
    return apiCall('/payments/send/card', {
      method: 'POST',
      body: JSON.stringify(transferData),
    });
  },

  getPaymentHistory: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return apiCall(`/payments/history?${params}`);
  },
};
// UPDATE this line to include paymentsAPI
export default {
  authAPI,
  walletAPI,
  hotWalletAPI,
  cardAPI,
  paymentsAPI,  // ← Add this
};